import H3 from "@/components/ui/headings/H3";
import H4 from "@/components/ui/headings/H4";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useTranslations } from "@/lib/static-intl";
import { useEffect, useRef, useState } from "react";
import { isApiPlace } from "@/utils/placeUtils";
import { buildPlaceDetailPath } from "@/constants/routes";
import { VerifiedStoreBadge } from "@/components/kit";

const PDW_PlaceDescription = ({
  place,
  favLists,
  className,
  isCssForDialog,
  openPlaceFavModal,
  userLocation,
  onOpenDetailPage,
  // Mobile place-detail (S3) 네이버식 히어로 재배치용 렌더 분할 플래그.
  // 기본값(undefined)일 때는 기존과 100% 동일하게 전체(정보+액션) 렌더 →
  // 데스크톱·다른 소비처는 영향 없음. 모바일 client.jsx만 이 값을 넘겨
  // 이름/평점("headOnly")을 썸네일 row 위에, 저장/길찾기/공유("actionsOnly")를
  // 그 아래에 나눠 렌더한다.
  renderMode,
}) => {
  const { user, openLoginModal } = useAuthContext();

  const showHead = renderMode !== "actionsOnly";
  const showActions = renderMode !== "headOnly";
  const isMobileActions = renderMode === "actionsOnly";
  // Mobile place-detail (S3) 네이버식 head: category as pill chips + larger
  // extrabold title, matching the approved mock. Gated on renderMode==="headOnly"
  // (only the mobile client.jsx passes it) so desktop / other consumers keep the
  // original plain-text head.
  const isMobileHead = renderMode === "headOnly";

  // 동네(area) chip source: explicit area fields, else parse the address for a
  // 동/읍/면/역 (then 구/군/시) token. Display-only; never mutates data.
  const placeArea =
    place?.seoArea ||
    place?.area ||
    place?.dong ||
    place?.stationName ||
    place?.locality ||
    (() => {
      const addr =
        place?.address || place?.roadAddress || place?.old_address || "";
      const parts = String(addr).split(" ").filter(Boolean);
      return (
        parts.find((p) => /동$|읍$|면$|역$/.test(p)) ||
        parts.find((p) => /구$|군$|시$/.test(p)) ||
        ""
      );
    })();
  const categoryChips = [place?.type, place?.subcategory, placeArea]
    .map((c) => (typeof c === "string" ? c.trim() : c))
    .filter(Boolean);

  const t = useTranslations("places.place_details_window");

  // NAVER rating summary (imported places). Shown only when Naver data exists.
  const naverRatingValue = Number(place?.naverRating);
  const hasNaverRating = Number.isFinite(naverRatingValue) && naverRatingValue > 0;
  const naverVisitorCount = Math.round(Number(place?.numberOfVisitorReviews));
  const hasNaverVisitorCount =
    Number.isFinite(naverVisitorCount) && naverVisitorCount > 0;
  // 사장님이 직접 운영하는 계정으로 승인되면 상호 옆에 인증 배지가 붙는다.
  const isVerifiedStore = Boolean(place?.storeAccountVerifiedAt);
  const hasNaverData = hasNaverRating || hasNaverVisitorCount;

  // Mobile head (S3 craft) rating row: show the ★score whenever a rating number
  // exists — prefer the Naver rating, else the DB average — plus the review count
  // (Naver visitor reviews, else DB reviews). Fixes the "리뷰 284, no score" case.
  const dbRatingValue = Number(place?.averageDbRating);
  const hasDbRating = Number.isFinite(dbRatingValue) && dbRatingValue > 0;
  const starScore = hasNaverRating
    ? naverRatingValue.toFixed(1)
    : hasDbRating
      ? dbRatingValue.toFixed(1)
      : null;
  const dbReviewCount = Math.round(Number(place?.totalDbReviews));
  const hasDbReviewCount = Number.isFinite(dbReviewCount) && dbReviewCount > 0;
  const reviewCount = hasNaverVisitorCount
    ? naverVisitorCount
    : hasDbReviewCount
      ? dbReviewCount
      : null;
  const hasMobileRatingRow = starScore != null || reviewCount != null;

  const titleButtonRef = useRef(null);
  // Prefer the freshly-fetched saved state (favLists) so the star reflects the
  // REAL saved state on load; fall back to place.PlaceFavorite when the parent
  // doesn't provide it. (Previously this was frozen once via useState, so it
  // never reflected the already-saved state on re-entry.)
  const placeFavList = favLists ?? place?.PlaceFavorite ?? [];
  const [showAllListItems, setShowAllListItems] = useState(false);
  const [isShareAddressPopupOpen, setIsShareAddressPopupOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const getPlaceShareUrl = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${buildPlaceDetailPath(place)}`;
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(getPlaceShareUrl()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleStarClick = () => {
    if (!user?.userId) {
      openLoginModal();
      return;
    }
    openPlaceFavModal();
  };

  useEffect(() => {
    const node = titleButtonRef.current;
    if (!node || !onOpenDetailPage) return undefined;

    node.addEventListener("click", onOpenDetailPage);

    return () => {
      node.removeEventListener("click", onOpenDetailPage);
    };
  }, [onOpenDetailPage]);

  const handleRouteToNaverMap = () => {
    if (!place?.latitude || !place?.longitude || !userLocation) return;

    const userLat = userLocation.latitude;
    const userLng = userLocation.longitude;
    const placeLat = Number(place.latitude);
    const placeLng = Number(place.longitude);

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      const mobileWebUrl = `https://m.map.naver.com/route/car?sX=${userLng}&sY=${userLat}&sText=${encodeURIComponent('현재위치')}&eX=${placeLng}&eY=${placeLat}&eText=${encodeURIComponent(place?.name || '목적지')}`;

      const naverMapAppUrl = `nmap://navigation?dlat=${placeLat}&dlng=${placeLng}&dname=${encodeURIComponent(place?.name || '목적지')}&appname=${encodeURIComponent('MyApp')}`;

      const intentUrl = `intent://navigation?dlat=${placeLat}&dlng=${placeLng}&dname=${encodeURIComponent(place?.name || '목적지')}&appname=${encodeURIComponent('MyApp')}#Intent;scheme=nmap;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=com.nhn.android.nmap;end`;

      if (isAndroid) {
        try {
          window.location.href = intentUrl;
        } catch (e) {
          window.open(mobileWebUrl, '_blank');
        }
      } else if (isIOS) {
        const clickedAt = Date.now();

        const fallbackTimer = setTimeout(() => {
          if (Date.now() - clickedAt < 2000) {
            window.open(mobileWebUrl, '_blank');
          }
        }, 1500);
        const handleVisibilityChange = () => {
          if (document.hidden) {
            clearTimeout(fallbackTimer);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        try {
          window.location.href = naverMapAppUrl;
        } catch (e) {
          clearTimeout(fallbackTimer);
          window.open(mobileWebUrl, '_blank');
        }
        setTimeout(() => {
          clearTimeout(fallbackTimer);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }, 3000);
      } else {
        window.open(mobileWebUrl, '_blank');
      }
    } else {
      window.open(
        `https://map.naver.com/p/directions/${userLng},${userLat},현위치,,/${placeLng},${placeLat},${place?.name},,/-/car`,
        "_blank"
      );
    }
  };

  // Alternative simpler approach - if the above doesn't work, try this:
  // const handleRouteToNaverMapSimple = () => {
  //   if (!place?.latitude || !place?.longitude || !userLocation) return;

  //   const userLat = userLocation.latitude;
  //   const userLng = userLocation.longitude;
  //   const placeLat = Number(place.latitude);
  //   const placeLng = Number(place.longitude);

  //   // Check if mobile
  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  //   if (isMobile) {
  //     // Use mobile web version with proper parameters
  //     const mobileUrl = `https://m.map.naver.com/route/car?sX=${userLng}&sY=${userLat}&sText=${encodeURIComponent('현재위치')}&eX=${placeLng}&eY=${placeLat}&eText=${encodeURIComponent(place?.name || '목적지')}`;
  //     window.open(mobileUrl, '_blank');
  //   } else {
  //     // Desktop version
  //     window.open(
  //       `https://map.naver.com/p/directions/${userLng},${userLat},현위치,,/${placeLng},${placeLat},${place?.name},,/-/car`,
  //       "_blank"
  //     );
  //   }
  // };
  const closureRisk = place?.localdataClosureRisk;
  const showClosureRisk = Boolean(closureRisk?.publicWarning);
  const isConfirmedClosed = closureRisk?.level === "CONFIRMED_CLOSED";
  const closureRiskLabel =
    closureRisk?.label ||
    (isConfirmedClosed
      ? "\uD3D0\uC5C5 \uD655\uC778"
      : "\uC601\uC5C5 \uC815\uBCF4 \uD655\uC778 \uD544\uC694");
  const closureRiskMessage =
    closureRisk?.message ||
    "\uC601\uC5C5 \uC815\uBCF4 \uD655\uC778\uC774 \uD544\uC694\uD55C \uC7A5\uC18C\uC785\uB2C8\uB2E4. \uBC29\uBB38 \uC804 \uC804\uD654\uB098 \uC9C0\uB3C4 \uC571\uC73C\uB85C \uD604\uC7AC \uC601\uC5C5 \uC5EC\uBD80\uB97C \uD655\uC778\uD558\uC138\uC694.";
  const closureRiskEyebrow = isConfirmedClosed ? "방문 주의" : "영업 정보 확인";
  // Mobile single-scroll: a "폐업 가능성" (NOT confirmed) place shows only a small
  // chip up here; the full sentence + 공공데이터 폐업일 now live in 위치·정보, right
  // under the phone number — the place the user actually acts on to verify. The
  // chip scrolls there. Confirmed closures keep the loud card (see render below).
  const showMobileClosureChip =
    isMobileHead && showClosureRisk && !isConfirmedClosed;
  const scrollToClosureDetail = () => {
    if (typeof document === "undefined") return;
    document
      .querySelector('[data-navid="location"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // 폐업 확정은 '경고'이지 '행동'이 아니다 — 브랜드 핵크(red) 대신 danger.
  const closureRiskClass = isConfirmedClosed
    ? "border-danger/40 bg-danger/10 text-danger shadow-sm ring-1 ring-danger/15"
    : "border-orange-300 bg-orange-50 text-orange-900 shadow-sm ring-1 ring-orange-100 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-100";

  return (
    <div className={`flex flex-col gap-2 relative ${className}`}>
      {showHead && user && user?.role === "admin" && !isApiPlace(place) && (
        <button
          type="button"
          // 편집은 보조 행동이다. 핑크 알약이라 화면에서 가장 세게 보였다 —
          // 한 화면의 행동색은 "매장 계정 만들기" 하나만 남긴다(디자인 기준 v2).
          className="absolute top-0 right-3 -translate-y-0 z-30 flex items-center justify-center gap-1 rounded-full border border-ink-25 bg-surface px-2 py-1 text-t-label text-ink dark:border-ink_on_dark-25 dark:bg-surface-2dark dark:text-ink_on_dark"
          onClick={() => {
            window.open(`/places/edit/${place?.id || place?._id || place?.contentid || place?.contentId}`, "_blank");
          }}
        >
          <Iconify
            icon={"heroicons-outline:pencil-alt"}
            className={"!text-inherit !min-w-3 !w-3 !h-3"}
          />
          <span className="text-inherit">{t("edit")}</span>
        </button>
      )}
      {showHead && isMobileHead && (
        <div className="w-full pt-3 px-[14px]">
          <h1 className="text-[18px] font-extrabold tracking-[-0.02em] text-black dark:text-white leading-tight text-start">
            {place?.name}
            {isVerifiedStore && (
              <VerifiedStoreBadge size={18} className="ml-1 align-[-3px]" />
            )}
          </h1>
          {(categoryChips.length > 0 || showMobileClosureChip) && (
            <div className="flex flex-wrap gap-[5px] mt-[6px]">
              {categoryChips.map((chip, i) => (
                <span
                  key={`${chip}-${i}`}
                  className="text-[11px] font-bold text-black/60 dark:text-white/60 bg-black/[0.04] dark:bg-white/[0.06] border border-black/[0.08] dark:border-white/[0.12] rounded-full px-2 py-[3px]"
                >
                  {chip}
                </span>
              ))}
              {showMobileClosureChip && (
                <button
                  type="button"
                  onClick={scrollToClosureDetail}
                  data-testid="place-closure-chip"
                  className="inline-flex items-center gap-[3px] text-[11px] font-bold rounded-full px-2 py-[3px] border border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-200"
                >
                  <Iconify
                    icon={"mdi:alert-circle-outline"}
                    className={"!w-[12px] !h-[12px] !min-w-[12px] !text-inherit"}
                  />
                  {"영업 확인 필요"}
                </button>
              )}
            </div>
          )}
          {hasMobileRatingRow && (
            <div className="flex items-center gap-[6px] mt-[9px] text-[12.5px]">
              {starScore != null && (
                <span className="font-extrabold text-ink dark:text-ink_on_dark">
                  <span className="text-star">{"★ "}</span>
                  {starScore}
                </span>
              )}
              {reviewCount != null && (
                <span className="text-ink-70 dark:text-ink_on_dark-70">
                  {"리뷰 "}
                  {reviewCount}
                </span>
              )}
              {hasNaverData && (
                <span className="text-[11px] text-ink-45 dark:text-ink_on_dark-45 bg-surface-2 dark:bg-surface-2dark rounded-[5px] px-[5px] py-[1px]">
                  {"네이버"}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {showHead && !isMobileHead && (
      <div
        className={`w-full flex flex-col items-start justify-between tracking-tight`}
      >
        <div
          className={`flex ${isCssForDialog
            ? "w-full flex-col items-start"
            : "w-fit flex-col items-start"
            } gap-1`}
        >
          {onOpenDetailPage ? (
            <button
              type="button"
              ref={titleButtonRef}
              onClick={onOpenDetailPage}
              className="max-w-full text-left"
            >
              {isCssForDialog ? (
                <H4 className={"!font-bold text-wrap text-start"}>
                  {place?.name}
                  {isVerifiedStore && (
                    <VerifiedStoreBadge size={18} className="ml-1 align-[-3px]" />
                  )}
                </H4>
              ) : (
                <H3 className={"!font-bold text-wrap text-start"}>
                  {place?.name}
                  {isVerifiedStore && (
                    <VerifiedStoreBadge size={18} className="ml-1 align-[-3px]" />
                  )}
                </H3>
              )}
            </button>
          ) : isCssForDialog ? (
            <H4 className={"!font-bold text-wrap text-start"}>
                  {place?.name}
                  {isVerifiedStore && (
                    <VerifiedStoreBadge size={18} className="ml-1 align-[-3px]" />
                  )}
                </H4>
          ) : (
            <H3 className={"!font-bold text-wrap text-start"}>
                  {place?.name}
                  {isVerifiedStore && (
                    <VerifiedStoreBadge size={18} className="ml-1 align-[-3px]" />
                  )}
                </H3>
          )}
          <div className="w-fit flex items-center justify-between gap-2">
            <span className="text-black/40 dark:text-white/60 text-nowrap">
              {place?.type}
              {place?.subcategory && `,${place?.subcategory}`}
            </span>
          </div>
          {hasNaverData && (
            <div className="w-fit flex items-center gap-1 text-sm font-semibold dark:text-white">
              {hasNaverRating && (
                <span className="flex items-center">
                  <Iconify
                    icon={"ic:round-star"}
                    className={"!text-amber-400 !w-4 !h-4 mr-0.5"}
                  />
                  {naverRatingValue.toFixed(1)}
                </span>
              )}
              {hasNaverRating && hasNaverVisitorCount && (
                <Iconify icon={"mdi:dot"} className={"!opacity-50"} />
              )}
              {hasNaverVisitorCount && (
                <span className="font-medium text-black/60 dark:text-white/70">
                  {"리뷰 "}
                  {naverVisitorCount}
                </span>
              )}
              <Iconify icon={"mdi:dot"} className={"!opacity-50"} />
              <span className="font-medium text-black/40 dark:text-white/50">
                {"네이버"}
              </span>
            </div>
          )}
        </div>
        {place?.amenities && (
          <span className="text-black/30 dark:text-white/50 text-nowrap text-xs w-full max-w-[80%] truncate">
            {place?.amenities}
          </span>
        )}
      </div>
      )}
      {showHead && showClosureRisk && (!isMobileHead || isConfirmedClosed) && (
        <div
          role="alert"
          aria-live="polite"
          data-testid="place-closure-warning"
          className={`w-full rounded-lg border px-3 py-3 text-sm leading-snug ${closureRiskClass}`}
        >
          <div className="flex items-start gap-2">
            <Iconify
              icon={"mdi:alert-circle-outline"}
              className={"mt-0.5 !min-w-6 !w-6 !h-6 !text-inherit"}
            />
            <div className="min-w-0">
              <div className="mb-1 inline-flex rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold">
                {closureRiskEyebrow}
              </div>
              <div className="text-base font-bold">{closureRiskLabel}</div>
              <div className="mt-1 font-medium">{closureRiskMessage}</div>
              {closureRisk?.latestClosedDate && (
                <div className="mt-1 text-xs opacity-80">
                  {"\uACF5\uACF5\uB370\uC774\uD130 \uD3D0\uC5C5\uC77C: "}
                  {closureRisk.latestClosedDate}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showHead && !isMobileHead && (
      <div className="w-full flex items-center">
        {Number(place?.averageDbRating || 0) > 0 && (
          <span className="text-nowrap flex text-sm font-semibold items-center dark:text-white">
            <Iconify
              icon={"ic:outline-star"}
              className={"!text-amber-400 !w-4 !h-4 mr-0.5"}
            />
            {place?.averageDbRating}
            <Iconify icon={"mdi:dot"} className={"!opacity-50"} />
          </span>
        )}
        {Number(place?.totalDbReviews) > 0 && (
          <p className="tracking-tight text-xs dark:text-white">
            {t("visitor reviews")} {Math.round(Number(place?.totalDbReviews))}
          </p>
        )}
      </div>
      )}
      {showActions && (
      <div className="w-full flex flex-col">
        <div
          className={
            isMobileActions
              ? "relative grid grid-cols-3 border-y border-black/[0.05] dark:border-white/[0.08]"
              : "w-full border-y border-black/[0.08] dark:border-white/[0.14] grid grid-cols-3 divide-x divide-black/[0.08] dark:divide-white/[0.14] py-2 text-black/70 dark:text-white/70 relative"
          }
        >
          <button
            type="button"
            onClick={handleStarClick}
            // 저장하기 전에도 핑크였다. 핑크는 '지금 할 수 있는 행동'이 아니라
            // '이미 한 상태'를 알려주는 자리에 쓰면 안 된다 — 저장한 뒤에만 켠다.
            className={
              isMobileActions
                ? `flex flex-col items-center gap-[3px] py-[10px] text-t-label ${
                    placeFavList?.length > 0
                      ? "text-action"
                      : "text-ink-70 dark:text-ink_on_dark-70"
                  }`
                : "py-4 flex flex-col items-center justify-center text-inherit"
            }
          >
            {placeFavList?.length > 0 ? (
              <Iconify
                icon={"mingcute:star-fill"}
                className={isMobileActions ? "!text-inherit !w-4 !h-4" : "!text-action"}
              />
            ) : (
              <Iconify
                icon={"tabler:star"}
                className={isMobileActions ? "!text-inherit !w-4 !h-4" : "!text-inherit"}
              />
            )}
            {t("star")}
          </button>
          <button
            type="button"
            onClick={handleRouteToNaverMap}
            className={
              isMobileActions
                ? "flex flex-col items-center gap-[3px] py-[10px] text-t-label text-ink-70 dark:text-ink_on_dark-70 border-l border-hairline dark:border-hairline-dark"
                : "py-4 flex flex-col items-center justify-center text-inherit"
            }
          >
            <Iconify
              icon={"heroicons:map-pin-16-solid"}
              className={isMobileActions ? "!text-inherit !w-4 !h-4" : "!text-inherit"}
            />
            {t("street view")}
          </button>
          <button
            type="button"
            onClick={() => setIsShareAddressPopupOpen((prev) => !prev)}
            className={
              isMobileActions
                ? "flex flex-col items-center gap-[3px] py-[10px] text-[11px] font-bold text-black/60 dark:text-white/60 border-l border-black/[0.05] dark:border-white/[0.08]"
                : "py-4 flex flex-col items-center justify-center text-inherit"
            }
          >
            <Iconify
              icon={"fluent:share-20-regular"}
              className={isMobileActions ? "!text-inherit !w-4 !h-4" : "!text-inherit"}
            />
            {t("share")}
          </button>
          {isShareAddressPopupOpen && (
            <div className="absolute z-[40] bg-white dark:bg-brown shadow-md border dark:border-brown rounded-lg top-0 left-0 translate-y-16 w-full py-3 px-4 flex flex-col gap-4">
              <div className="w-full flex items-center justify-center">
                <span className="w-full font-semibold text-lg text-center text-black dark:text-white tracking-tighter">
                  {t("share")}
                </span>
                <button
                  type="button"
                  onClick={() => setIsShareAddressPopupOpen((prev) => !prev)}
                >
                  <Iconify icon={"grommet-icons:close"} />
                </button>
              </div>
              <div className="w-full flex rounded-lg overflow-hidden border dark:bg-dark_bg_grey dark:border-brown divide-x dark:divide-brown">
                <div className="w-full max-w-[83%] text-nowrap truncate p-2 bg-white dark:bg-transparent text-sm text-black/45 dark:text-white/50 cursor-text tracking-tight">
                  {getPlaceShareUrl()}
                </div>
                <button
                  type="button"
                  className="px-2 text-xs text-nowrap"
                  onClick={handleCopyToClipboard}
                >
                  {copied ? t("copied") : t("copy")}
                </button>
              </div>
            </div>
          )}
        </div>
        {placeFavList?.length > 0 && (
          <div className="w-full flex items-center justify-center gap-2 flex-wrap py-2 px-2">
            {(showAllListItems ? placeFavList : placeFavList?.slice(0, 1))?.map(
              (_pl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() =>
                    window.open(
                      `/places/fav-list/${_pl?.placeFavoriteList?.id}`,
                      "_blank"
                    )
                  }
                  className="w-fit border dark:border-brown rounded-full flex items-center justify-center gap-1 px-2 py-1.5 scale-90"
                >
                  <span
                    style={{
                      backgroundColor:
                        _pl?.placeFavoriteList?.color || "#FFC107",
                    }}
                    className="w-5 min-w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    <Iconify
                      icon={"material-symbols:star-rounded"}
                      className={"!text-white !min-w-4 !w-4 !h-4"}
                    />
                  </span>
                  <span className="text-nowrap text-sm font-medium">
                    {_pl?.placeFavoriteList?.listName}
                  </span>
                </button>
              )
            )}
            {placeFavList?.length > 1 && !showAllListItems && (
              <button
                type="button"
                onClick={() => setShowAllListItems((prev) => !prev)}
                className="w-fit flex items-center justify-center gap-1 text-sm text-black/45 dark:text-white/50 tracking-tight text-nowrap"
              >
                <span className="text-nowrap">
                  {t("saved_in")} {placeFavList?.length - 1}{" "}
                  {t("other_locations_expand")}
                </span>
                <Iconify
                  icon={"formkit:down"}
                  className={"!w-4 !h-4 !text-inherit"}
                />
              </button>
            )}
            {(placeFavList?.length === 1 || showAllListItems) && (
              <button
                type="button"
                onClick={handleStarClick}
                className="w-fit flex items-center justify-center gap-1 text-sm text-black/45 dark:text-white/50 tracking-tight text-nowrap"
              >
                <span>{t("edit")}</span>
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PDW_PlaceDescription;
